import { AITool } from "@/comps/editors/deform"
import useAuth, {
	getUserEntitlements,
} from "@/utils/client-auth"
import queryfy from "@/utils/queryfy"
import axios, { AxiosError } from "axios"
import { useRouter } from "next/router"
import { indexFiltersAdExamples } from "pages/ad-examples"
import {
	AdExamplesType,
	AvailableFilters,
	AvailableFiltersTemplates,
	FilterStateAdExamples,
	FilterStateGallery,
	FilterStateTemplates,
	indexFiltersGallery,
	TemplatesWithSections,
} from "pages/ai-gallery"
import { CaptionsResponse } from "pages/api/caption-templates"
import { HistoryEntity } from "pages/api/deforum-history"
import { GalleryResponse } from "pages/api/gallery-all"
import { GalleryFiltersResponse } from "pages/api/gallery-filters"
import { TutorialFiltersResponse } from "pages/api/tutorial-filters"
import { WizardGetInputsResponse } from "pages/api/wizard-get-inputs"
import {
	StartSessionRequest,
	StartSessionResponse,
} from "pages/api/wizard-start-session"
import { StartSessionWithInputsRequest } from "pages/api/wizard-start-session-with-inputs"
import { indexFiltersTemplates } from "pages/video-templates"
import {
	Reducer,
	useCallback,
	useEffect,
	useReducer,
	useRef,
} from "react"
import { useQuery, UseQueryResult } from "react-query"
import { Pages } from "sections/gallery/all-gallery"
import {
	fetchLast,
	fetchVideos,
	getConcurrentJobCount,
	STATUS_UPDATE_MILLIS,
} from "sections/history/generations"
import { z } from "zod"
import { JobStatusesResponse } from "../pages/api/wizard-job-statuses"
import {
	ChatListResponse,
	chatMessageSchema,
	ChatRequestImage,
	chatRequestSchema,
	ChatRequestSourceMedia,
	ChatRequestVideo,
	chatResponseEditSegmentsSchema,
	chatResponseFullSchema,
	chatResponseMediaFullSchema,
	chatResponseSchema,
} from "../pages/api/wizard-list-chat"
import {
	SendMessageRequest,
	SendMessageResponse,
} from "../pages/api/wizard-send-message"
import { WizardUploadResponse } from "../pages/api/wizard-upload"
import { dateToSortableString } from "../utils/dates"
import { isStringUrl } from "../utils/functions"
import { mimeTypeMapper } from "../utils/upload-file"
import {
	getLoadedImage,
	getLoadedVideo,
	getVideoDurationMillis,
} from "../utils/video"

export const SESSION_LIMIT = 15

export interface WizardProps {
	initialSessionId: number | null
	initialMessage: string | null
	sessions: SessionType[]
	group: {
		id: number
		name: string
	} | null
}

export type PageName =
	| "home"
	| "videoGpt"
	| "chat"
	| "discover"

export type PagesUrlNames =
	| "home"
	| "video-gpt"
	| "chat"
	| "discover"

export type CombinedPopupsData = WizardGetInputsResponse & {
	captions: CaptionsResponse
}

export type HomePageType = {
	content: SessionType[]
	projectsDisplay: "column" | "grid"
	text: string
	popupsData: CombinedPopupsData | null
}

export type AiStudioPageType = {
	content: HistoryEntity[]
	filter: AITool[]
	selectedTool: AITool | null
}

type ChatContent = Record<number, SessionType | undefined>

export type ChatPageType = {
	content: ChatContent
	sessionsGroup: SessionsGroupType[]
	selectedSession: number | null
	initialMessage: string | null
	initialInputs: StartSessionWithInputsRequest | null
	group: {
		id: number
		name: string
	} | null
}

export type DiscoverPageProps = {
	initialGallery: GalleryResponse | null
	initialFiltersGallery: FilterStateGallery | null
	initialTemplates: TemplatesWithSections[] | null
	initialFiltersTemplates: FilterStateTemplates | null
	initialAdExamples: AdExamplesType | null
	initialFiltersAdExamples: FilterStateAdExamples | null
	availableFiltersGallery: AvailableFilters | null
	availableFiltersTemplates: AvailableFiltersTemplates | null
}

export type DiscoverPageType = {
	content: DiscoverPageProps
	filter: Pages
	q: string | null
	categoryId: string | null
}

export type SelectedMediaMessageType =
	| ChatRequestImage
	| ChatRequestVideo
	| null

export interface CompletedMessageProps {
	videoUrl: string
	videoWidth: number
	videoHeight: number
	resultId: string
	videoDesc: string
	shareUrl: string
	previewImage: string
	title: string
}

export type MediasFullType = z.infer<
	typeof chatResponseMediaFullSchema
>

export type SegmentsUploadFileType = {
	media_type: "image" | "video"
	resource_url: string
	aspect: number
	thumb_url: string | null
	blob?: Blob
	index?: number
	isVideoLengthMatched?: boolean
} | null

export type UploadedMediaType =
	| { status: "non_upload"; medias: null }
	| {
			medias: SegmentsUploadFileType[]
			status: "fail" | "idle" | "uploading" | "done"
	  }

export type PlatformRequestType =
	| "demo"
	| "story"
	| "ads"
	| "reel"

export type SessionStatus =
	| "idle"
	| "waiting"
	| "visualizing"
	| "generating"
	| "completed"
	| "loading"
	| "error"
	| "deleted"
	| "analyzing"
	| "client-loading"
	| "retryable_error"

export type ErrorType =
	| "upgrade"
	| "unknown"
	| "retry"
	| "upgrade-tokens"

export type SessionType = {
	id: number
	name: string
	messages: ChatMessage[] | null
	started: DateCategories
	status: SessionStatus
	interacted_at: string
	errorType?: ErrorType
	uploadedMedias: UploadedMediaType
	thumbnail_url: string | null
	showRating?: boolean
}

export type ChatRequest = z.infer<typeof chatRequestSchema>
export type ChatResponse = z.infer<
	typeof chatResponseSchema
>
export type ChatResponseFull = z.infer<
	typeof chatResponseFullSchema
>

export type ChatMessage = z.infer<typeof chatMessageSchema>

export type SessionsGroupType = {
	sessions: {
		id: number
		name: string
		interacted_at: string
	}[]
	started: string
}

export type ChatReducerState = {
	sessionsGroup: SessionsGroupType[]
	selectedSession: number | null
	sessions: Record<number, SessionType | undefined>
}

export type DashboardReducerAction =
	| {
			name: "home_state"
			history: HistoryEntity[]
			filter?: AITool[]
	  }
	| {
			name: "video_gpt_state"
			projectList: SessionType[]
	  }
	| {
			name: "discover_state"
			availableFiltersGallery: GalleryFiltersResponse
			availableFiltersTemplates: TutorialFiltersResponse
	  }
	| {
			name: "chat_state"
			content: ChatContent
	  }
	| {
			name: "add_ai_studio_content"
			newContent: HistoryEntity[]
	  }
	| {
			name: "add_ai_studio_content_from_start"
			newContent: HistoryEntity[]
	  }
	| {
			name: "add_video_gpt_content"
			newContent: SessionType[]
	  }
	| {
			name: "set_video_gpt_text"
			text: string
	  }
	| {
			name: "change_projects_display"
			display: "column" | "grid"
	  }
	| {
			name: "change_discover_filter"
			filter: Pages
			q?: string
			categoryId?: string
	  }
	| {
			name: "remake_session"
			remaked: SessionType
	  }
	| {
			name: "chat_with_initial_message"
			initialMessage: string
	  }
	| {
			name: "select_ai_studio_tool"
			selectedTool: AITool | null
	  }
	| {
			name: "delete_history_item"
			id: number
	  }
	| {
			name: "load_popups_data"
			data: WizardGetInputsResponse
			captions: CaptionsResponse
	  }
	| {
			name: "change_child_name"
			childName: string | null
	  }
	| {
			name: "chat_empty_state"
	  }
	| {
			name: "chat_set_session_status"
			sessionId: number
			sessionStatus: SessionStatus
			errorType?: ErrorType
			temporaryMessageId?: number
	  }
	| {
			name: "chat_add_new_session"
			newSession: SessionType
	  }
	| {
			name: "chat_select_session"
			sessionId: number
	  }
	| {
			name: "chat_change_session_name"
			sessionId: number
			newName: string
	  }
	| {
			name: "chat_delete_session"
			sessionId: number
	  }
	| {
			name: "chat_add_new_group"
			newSessions: SessionType[]
	  }
	| {
			name: "chat_update_sessions"
			updatedSessions: number[]
	  }
	| {
			name: "chat_add_request"
			sessionId: number
			request: ChatRequest
			temporaryId: number
			interacted_at: string
			started: DateCategories
	  }
	| {
			name: "chat_add_response"
			status: SessionStatus
			response: ChatResponse
			responseId: number
			sentAt: string
			requestUpdate: {
				id: number
				realId: number
				sentAt: string
			}
			sessionId: number
			newSessionName: string | null
			sessionNameChanged: boolean
	  }
	| {
			name: "chat_populate_messages"
			sessionStatus: SessionStatus
			id: number
			messages: ChatMessage[]
			sessionName: string | null
			sessionInteractedAt: string
			errorType?: ErrorType
	  }
	| {
			name: "chat_change_temp_session"
			newSession: SessionType
			temporaryId: number
	  }
	| {
			name: "chat_update_uploaded_files"
			uploadedMedias: UploadedMediaType
			sessionId: number
	  }
	| {
			name: "chat_reset_session"
			messages: ChatMessage[]
			sessionId: number
			status: SessionStatus
	  }
	| {
			name: "chat_retry_session"
			sessionId: number
			temporaryMessageId: number
			status: SessionStatus
	  }
	| {
			name: "chat_change_rating_value"
			id: number
	  }

const dashboardReducer: Reducer<
	DashboardReducerState,
	DashboardReducerAction
> = (state, action): DashboardReducerState => {
	function updateSession(
		id: number,
		updater: (old: SessionType) => SessionType,
		realState?: ChatPageType,
	): ChatPageType {
		if (state.activePage !== "chat") {
			return state.chat
		}

		const currentState = realState ?? state.chat
		const currentSession = currentState.content[id]
		if (currentSession === undefined) {
			console.error("Updating session that does not exist")
			return currentState
		}

		let updatedGroups = currentState.sessionsGroup
		const newSession = updater(currentSession)
		if (
			newSession.interacted_at !==
				currentSession.interacted_at ||
			newSession.status !== currentSession.status
		) {
			updatedGroups = extractSessionsToGroups(
				[newSession],
				updatedGroups,
			)
		}

		return {
			...currentState,
			sessionsGroup: updatedGroups,
			content: {
				...currentState.content,
				[id]: newSession,
			},
		}
	}

	switch (action.name) {
		case "video_gpt_state":
			return {
				...state,
				activePage: "videoGpt",
				videoGpt: {
					content: action.projectList,
					text: "",
					projectsDisplay: "column",
					popupsData: state.videoGpt.popupsData,
				},
			}

		case "chat_state":
			return {
				...state,
				activePage: "chat",
				chat: {
					...state.chat,
					content: action.content,
					sessionsGroup: extractSessionsToGroups(
						Object.values(action.content)
							.map((s) => s)
							.filter(Boolean),
						state.chat.sessionsGroup,
					),
				},
			}

		case "home_state":
			return {
				...state,
				activePage: "home",
				home: {
					content: action.history,
					filter: action.filter ?? [
						"deform",
						"ai_restyle",
						"ai_photo",
						"text_to_image",
						"text_to_video",
					],
					selectedTool: null,
				},
			}

		case "discover_state":
			return {
				...state,
				activePage: "discover",
				discover: {
					filter: "ai-gallery",
					q: null,
					categoryId: null,
					content: {
						initialFiltersGallery: indexFiltersGallery,
						initialFiltersTemplates: indexFiltersTemplates,
						initialFiltersAdExamples:
							indexFiltersAdExamples,
						initialTemplates: null,
						availableFiltersTemplates:
							action.availableFiltersTemplates,
						initialGallery: null,
						availableFiltersGallery:
							action.availableFiltersGallery,
						initialAdExamples: null,
					},
				},
			}

		case "add_ai_studio_content":
			if (state.activePage !== "home") {
				return state
			}
			const addedHistoryContent = filterAndConcatHistory(
				state.home.content,
				action.newContent,
				false,
			)

			return {
				...state,
				activePage: "home",
				home: {
					...state.home,
					content: addedHistoryContent,
				},
			}

		case "add_ai_studio_content_from_start": {
			if (state.activePage !== "home") {
				return state
			}
			const addedHistoryContent = filterAndConcatHistory(
				state.home.content,
				action.newContent,
				true,
			)

			return {
				...state,
				activePage: "home",
				home: {
					...state.home,
					content: addedHistoryContent,
				},
			}
		}

		case "add_video_gpt_content":
			if (state.activePage !== "videoGpt") {
				return state
			}
			const addedContent = removeIfExist(
				state.videoGpt.content,
				action.newContent,
			)

			return {
				...state,
				videoGpt: {
					...state.videoGpt,
					content: addedContent,
				},
			}

		case "set_video_gpt_text":
			if (state.activePage !== "videoGpt") {
				return state
			}

			return {
				...state,
				videoGpt: {
					...state.videoGpt,
					text: action.text,
				},
			}

		case "change_projects_display":
			if (state.activePage !== "videoGpt") {
				return state
			}

			return {
				...state,
				videoGpt: {
					...state.videoGpt,
					projectsDisplay: action.display,
				},
			}

		case "change_discover_filter":
			if (state.activePage !== "discover") {
				return state
			}

			return {
				...state,
				discover: {
					...state.discover,
					filter: action.filter,
					q: action.q ?? null,
					categoryId: action.categoryId ?? null,
				},
			}

		case "remake_session":
			if (state.activePage !== "videoGpt") {
				return state
			}

			return {
				...state,
				videoGpt: {
					...state.videoGpt,
					content: [
						action.remaked,
						...state.videoGpt.content,
					],
				},
			}

		case "chat_with_initial_message":
			return {
				...state,
				activePage: "chat",
				chat: {
					...state.chat,
					initialMessage: action.initialMessage,
				},
			}

		case "select_ai_studio_tool":
			if (state.activePage !== "home") {
				return state
			}

			return {
				...state,
				home: {
					...state.home,
					selectedTool: action.selectedTool,
				},
			}

		case "delete_history_item":
			if (state.activePage !== "home") {
				return state
			}

			return {
				...state,
				home: {
					...state.home,
					content: state.home.content.filter(
						(i) => i.id !== action.id,
					),
				},
			}

		case "load_popups_data":
			if (state.activePage !== "videoGpt") {
				return state
			}

			return {
				...state,
				videoGpt: {
					...state.videoGpt,
					popupsData: {
						...action.data,
						captions: action.captions,
					},
				},
			}

		case "change_child_name":
			return {
				...state,
				activeChild: action.childName,
			}

		case "chat_empty_state":
			if (state.activePage !== "chat") {
				return state
			}

			return {
				...state,
				chat: {
					...state.chat,
					selectedSession: null,
				},
			}

		case "chat_set_session_status":
			if (
				state.activePage !== "chat" ||
				state.chat.content[action.sessionId]?.status ===
					"deleted"
			) {
				return state
			}

			return {
				...state,
				chat: updateSession(action.sessionId, (old) => {
					return {
						...old,
						status: action.sessionStatus,
						errorType: action.errorType,
					}
				}),
			}

		case "chat_update_uploaded_files":
			if (state.activePage !== "chat") {
				return state
			}

			return {
				...state,
				chat: updateSession(action.sessionId, (old) => {
					return {
						...old,
						uploadedMedias: action.uploadedMedias,
					}
				}),
			}

		case "chat_add_new_session":
			return {
				...state,
				activePage: "chat",
				chat: {
					...state.chat,
					selectedSession: action.newSession.id,
					content: {
						...state.chat.content,
						[action.newSession.id]: action.newSession,
					},
					sessionsGroup: extractSessionsToGroups(
						[action.newSession],
						state.chat.sessionsGroup,
					),
				},
				videoGpt: {
					...state.videoGpt,
					content: [
						action.newSession,
						...state.videoGpt.content,
					],
				},
			}

		case "chat_select_session":
			return {
				...state,
				chat: {
					...state.chat,
					selectedSession: action.sessionId,
				},
			}

		case "chat_change_session_name":
			const updated = updateSession(
				action.sessionId,
				(old) => {
					return {
						...old,
						name: action.newName,
					}
				},
			)

			return {
				...state,
				chat: {
					...updated,
					sessionsGroup: state.chat.sessionsGroup.map(
						(g) => {
							return {
								...g,
								sessions: g.sessions.map((s) => {
									if (s.id === action.sessionId) {
										return {
											...s,
											name: action.newName,
										}
									}
									return s
								}),
							}
						},
					),
				},
				videoGpt: {
					...state.videoGpt,
					content: state.videoGpt.content.map((s) => {
						if (s.id === action.sessionId) {
							return {
								...s,
								name: action.newName,
							}
						}
						return s
					}),
				},
			}

		case "chat_delete_session":
			return {
				...state,
				chat: updateSession(action.sessionId, (old) => {
					return {
						...old,
						status: "deleted",
					}
				}),
				videoGpt: {
					...state.videoGpt,
					content: state.videoGpt.content.map((s) => {
						if (s.id === action.sessionId) {
							return {
								...s,
								status: "deleted",
							}
						}
						return s
					}),
				},
			}

		case "chat_add_new_group":
			if (state.activePage !== "chat") {
				return state
			}
			const sessionsGroup = extractSessionsToGroups(
				action.newSessions,
				state.chat.sessionsGroup,
			)

			const addedSessions: Record<
				number,
				SessionType | undefined
			> = {}

			for (const newSession of action.newSessions) {
				if (!state.chat.content[newSession.id]) {
					addedSessions[newSession.id] = newSession
				}
			}

			return {
				...state,
				chat: {
					...state.chat,
					content: {
						...state.chat.content,
						...addedSessions,
					},
					sessionsGroup,
				},
			}

		case "chat_update_sessions":
			if (state.activePage !== "chat") {
				return state
			}

			return {
				...state,
				chat: action.updatedSessions.reduce(
					(oldState, id) =>
						updateSession(
							id,
							(oldSession) => {
								return {
									...oldSession,
									status: "waiting",
								}
							},
							oldState,
						),
					state.chat,
				),
			}

		case "chat_populate_messages":
			if (state.activePage !== "chat") {
				return state
			}

			return {
				...state,
				chat: updateSession(action.id, (old) => {
					const showRating =
						action.messages
							.map((m) => {
								if (
									m.type === "response" &&
									m.name === "completed"
								) {
									return m.generation.show_rating
								}
							})
							.filter(Boolean)
							.at(0) ?? false

					return {
						...old,
						status: action.sessionStatus,
						messages: action.messages,
						name: action.sessionName ?? old.name,
						errorType: action.errorType,
						interacted_at: action.sessionInteractedAt,
						showRating,
					}
				}),
			}

		case "chat_reset_session":
			if (state.activePage !== "chat") {
				return state
			}

			return {
				...state,
				chat: updateSession(action.sessionId, (old) => {
					return {
						...old,
						status: action.status,
						messages: action.messages,
					}
				}),
			}

		case "chat_retry_session":
			if (state.activePage !== "chat") {
				return state
			}

			return {
				...state,
				chat: updateSession(action.sessionId, (old) => {
					return {
						...old,
						errorType: undefined,
						status: action.status,
						messages:
							old.messages?.filter(
								(m) => m.id !== action.temporaryMessageId,
							) ?? null,
					}
				}),
			}

		case "chat_add_request":
			if (state.activePage !== "chat") {
				return state
			}

			return {
				...state,
				chat: updateSession(action.sessionId, (old) => {
					return {
						...old,
						started: action.started,
						interacted_at: action.interacted_at,
						status: "client-loading",
						messages: [
							{
								id: action.temporaryId,
								sent_at: dateToSortableString(new Date()),
								type: "request",
								...action.request,
							},
							...(old.messages ?? []),
						],
					}
				}),
			}

		case "chat_change_rating_value":
			const session = state.chat.content[action.id]
			if (session === undefined) {
				return state
			}

			return {
				...state,
				chat: {
					...state.chat,
					content: {
						...state.chat.content,
						[action.id]: {
							...session,
							showRating: false,
						},
					},
				},
			}

		case "chat_add_response":
			if (
				state.chat.content[action.sessionId]?.status ===
				"deleted"
			) {
				return state
			}
			return {
				...state,
				chat: updateSession(action.sessionId, (old) => {
					return {
						...old,
						status: action.status,
						name: action.newSessionName ?? old.name,
						showRating:
							action.response.name === "completed"
								? action.response.generation.show_rating
								: undefined,
						messages: [
							{
								id: action.responseId,
								sent_at: action.sentAt,
								type: "response",
								...action.response,
							},
							...(old.messages?.map((m) => {
								if (m.type !== "request") {
									return m
								}

								if (m.id !== action.requestUpdate.id) {
									return m
								}

								return {
									...m,
									sent_at: action.requestUpdate.sentAt,
									id: action.requestUpdate.realId,
								}
							}) ?? []),
						],
					}
				}),
			}

		case "chat_change_temp_session":
			const newGroups = extractSessionsToGroups(
				[action.newSession],
				state.chat.sessionsGroup,
			)

			newGroups[0].sessions = newGroups[0].sessions.filter(
				(s) => s.id !== action.temporaryId,
			)

			if (Number(state.chat.selectedSession) > 0) {
				return {
					...state,
					chat: {
						...state.chat,
						content: {
							...state.chat.content,
							[action.newSession.id]: action.newSession,
							[action.temporaryId]: undefined,
						},
						sessionsGroup: newGroups,
					},
					videoGpt: {
						...state.videoGpt,
						content: state.videoGpt.content.filter(
							(s) => s.id !== action.temporaryId,
						),
					},
				}
			} else {
				return {
					...state,
					chat: {
						...state.chat,
						selectedSession: action.newSession.id,
						content: {
							...state.chat.content,
							[action.newSession.id]: action.newSession,
							[action.temporaryId]: undefined,
						},
						sessionsGroup: newGroups,
					},
					videoGpt: {
						...state.videoGpt,
						content: state.videoGpt.content.filter(
							(s) => s.id !== action.temporaryId,
						),
					},
				}
			}
	}
}

export type DashboardControllerInitialData =
	| {
			name: "home"
			initialPage: AiStudioPageType
	  }
	| {
			name: "chat"
			initialPage: ChatPageType
	  }
	| {
			name: "discover"
			initialPage: DiscoverPageType
	  }
	| {
			name: "videoGpt"
			initialPage: HomePageType
	  }

type DashboardReducerState = {
	activePage: PageName
	activeChild: string | null
	home: AiStudioPageType
	videoGpt: HomePageType
	chat: ChatPageType
	discover: DiscoverPageType
}

export type LastQueryType = UseQueryResult<
	| {
			id: number
			status:
				| "canceled"
				| "failed"
				| "in-queue"
				| "in-progress"
				| "ready"
			thumbnail: string | null | undefined
			isVideo: boolean
			estimatedSeconds: number
			prompt: string
			tool:
				| "deform"
				| "ai_photo"
				| "ai_restyle"
				| "text_to_image"
				| "text_to_video"
				| "ai_suggest"
			model: number | null
	  }[]
	| null,
	unknown
>

export type ChatResponseEditSegmentsType = z.infer<
	typeof chatResponseEditSegmentsSchema
>

export interface ChatController {
	startNewSession: (
		options: StartSessionRequest["context"],
		inputs: StartSessionWithInputsRequest | null,
	) => Promise<void>
	enterShortCut: (id: number, name: string) => void
	loadMoreSessions: (offset: number) => Promise<number[]>
	selectSession: (id: number) => Promise<void>
	sendMessage: (request: ChatRequest) => Promise<void>
	deleteSession: (id: number) => Promise<void>
	renameSession: (
		id: number,
		newName: string,
	) => Promise<void>
	clearChatScreen: () => void
	appendFileCreator: (
		index: number,
		minDuration: number,
	) => (files: File[]) => void
	removeUploadedFile: (index: number) => void
	uploadAndSendMedias: (
		selectMediaCase: boolean,
	) => Promise<void>
	initializaeUploadedMedias: (length: number) => void
	resetSession: (next: boolean) => Promise<void>
	duplicateSession: () => Promise<void>
	remakeSession: (id: number) => Promise<void>
	retrySession: () => Promise<void>
	selectMediaCreator: (files: File[]) => void
	changeShowRatingValue: () => void
	profilePicture: string | null
	lastResponse: ChatResponseFull | null
	lastMessageId: number | null
}

export function createSessionsRecord(
	sessions: SessionType[],
): Record<number, SessionType | undefined> {
	const initial: Record<number, SessionType | undefined> =
		{}
	for (const session of sessions) {
		initial[session.id] = session
	}

	return initial
}

export interface DashboardController {
	changeSelectedPage: (pageName: PageName) => Promise<void>
	loadMoreHistoryContent: (
		offset: number,
	) => Promise<number[]>
	changeProjectsDisplay: (
		display: "column" | "grid",
	) => void
	changeDiscoverFilter: (
		filter: Pages,
		q?: string,
		categoryId?: string,
	) => void
	selectAiStudioTool: (tool: AITool | null) => void
	deleteHistoryItem: (id: number) => Promise<void>
	changeActiveChild: (name: string | null) => void
	activeChild: string | null
	activePage: PageName
	home: AiStudioPageType
	chat: ChatPageType
	videoGpt: HomePageType
	discover: DiscoverPageType
	canTryAgain: boolean
	lastQuery: LastQueryType
	chatController: ChatController
}

export function useDashboardController(
	initialData: DashboardControllerInitialData,
): DashboardController {
	const [state, dispatch] = useReducer(
		dashboardReducer,
		createPagesData(initialData),
	)
	const { userInfo } = useAuth()
	const router = useRouter()
	const lastMessage =
		state.chat.content[
			state.chat.selectedSession ?? -1
		]?.messages?.at(0)
	const lastMessageId =
		state.chat.content[
			state.chat.selectedSession ?? -1
		]?.messages?.at(0)?.id ?? null
	const lastResponse =
		lastMessage?.type === "response" ? lastMessage : null
	const isFirstTime = useRef<boolean>(true)

	function initializaeUploadedMedias(length: number) {
		const id = state.chat.selectedSession
		if (!id) return

		const initial = new Array(length).fill(null)

		dispatch({
			name: "chat_update_uploaded_files",
			sessionId: id,
			uploadedMedias: {
				medias: initial,
				status: "idle",
			},
		})
	}

	function appendFileCreator(
		index: number,
		minDuration: number,
	) {
		return async function (files: File[]) {
			const id = state.chat.selectedSession
			if (!id) return

			const uploadedMedias =
				state.chat.content[id]?.uploadedMedias?.medias

			if (
				uploadedMedias === undefined ||
				uploadedMedias === null
			)
				return

			const newUploaded: SegmentsUploadFileType[] =
				await Promise.all(
					uploadedMedias.map(async (elem, i) => {
						if (i === index) {
							const file = files[0]
							const isImage = file.type.includes("image")

							if (isImage) {
								const imgElem = await getLoadedImage(file)

								return {
									blob: file,
									aspect: imgElem.width / imgElem.height,
									isVideoLengthMatched: true,
									thumb_url: null,
									resource_url: URL.createObjectURL(file),
									media_type: "image",
								}
							} else {
								const video = await getLoadedVideo(file)

								const uploadedVideoLength =
									(await getVideoDurationMillis(file)) /
									1000
								const isVideoLengthMatched =
									uploadedVideoLength >= minDuration

								return {
									blob: file,
									aspect: video.width / video.height,
									isVideoLengthMatched,
									thumb_url: null,
									resource_url: URL.createObjectURL(file),
									media_type: "video",
								}
							}
						}
						return elem
					}),
				)

			dispatch({
				name: "chat_update_uploaded_files",
				uploadedMedias: {
					medias: newUploaded,
					status: "idle",
				},
				sessionId: id,
			})
		}
	}

	function removeUploadedFile(index: number) {
		const id = state.chat.selectedSession
		if (!id) return

		const uploadedMedias =
			state.chat.content[id]?.uploadedMedias?.medias

		if (
			uploadedMedias === undefined ||
			uploadedMedias === null
		) {
			return
		}
		const removed: SegmentsUploadFileType[] =
			uploadedMedias.map((elem, i) => {
				if (i === index) {
					return null
				}
				return elem
			})

		dispatch({
			name: "chat_update_uploaded_files",
			uploadedMedias: {
				medias: removed,
				status: "idle",
			},
			sessionId: id,
		})
	}

	async function selectMediaCreator(files: File[]) {
		const id = state.chat.selectedSession
		if (!id) return

		let newUploaded: SegmentsUploadFileType

		const file = files.at(0)
		if (!file) return

		const isImage = file.type.includes("image")

		if (isImage) {
			const imgElem = await getLoadedImage(file)

			newUploaded = {
				blob: file,
				aspect: imgElem.width / imgElem.height,
				resource_url: URL.createObjectURL(file),
				media_type: "image",
				thumb_url: null,
			}
		} else {
			const video = await getLoadedVideo(file)

			newUploaded = {
				blob: file,
				aspect: video.videoWidth / video.videoHeight,
				resource_url: URL.createObjectURL(file),
				thumb_url: null,
				media_type: "video",
			}
		}

		const temporaryMessageId = Math.round(
			Math.random() * -1_000_000,
		)

		let temporaryMedia: ChatRequestImage | ChatRequestVideo

		if (newUploaded.media_type === "video") {
			temporaryMedia = {
				media_type: "video",
				resource_url: newUploaded.resource_url,
				aspect: newUploaded.aspect,
				thumb_url: null,
			}

			dispatch({
				name: "chat_add_request",
				sessionId: id,
				request: {
					name: "video",
					choice: temporaryMedia,
					index: -1,
				},
				temporaryId: temporaryMessageId,
				interacted_at: dateToSortableString(new Date()),
				started: "Today",
			})
		} else {
			temporaryMedia = {
				media_type: "image",
				resource_url: newUploaded.resource_url,
				aspect: newUploaded.aspect,
			}
			dispatch({
				name: "chat_add_request",
				sessionId: id,
				request: {
					name: "image",
					choice: temporaryMedia,
					index: -1,
				},
				temporaryId: temporaryMessageId,
				interacted_at: dateToSortableString(new Date()),
				started: "Today",
			})
		}

		if (!newUploaded.blob) {
			throw new Error("")
		}
		const extension = mimeTypeMapper[newUploaded.blob?.type]
		if (!extension) {
			throw new Error("file not supported")
		}

		const urls = await axios
			.post<WizardUploadResponse>("/api/wizard-upload", {
				extension,
				session_id: id,
			})
			.then((r) => r.data)

		await axios.put(urls.upload ?? "", newUploaded.blob)

		if (isImage) {
			await sendMessage(
				{
					name: "media_source",
					medias: [
						{
							media_type: "image",
							resource_url: urls?.download ?? "",
							aspect: newUploaded.aspect,
						},
					],
				},
				true,
			)
		} else {
			await sendMessage(
				{
					name: "media_source",
					medias: [
						{
							media_type: "video",
							resource_url: urls?.download ?? "",
							aspect: newUploaded.aspect,
							thumb_url: null,
						},
					],
				},
				true,
			)
		}
	}

	async function uploadAndSendMedias() {
		const id = state.chat.selectedSession
		if (!id) return

		const uploadedMedias =
			state.chat.content[id]?.uploadedMedias?.medias
		if (!uploadedMedias || uploadedMedias.length === 0) {
			return
		}
		const temporaryMessageId = Math.round(
			Math.random() * -1_000_000,
		)

		const temporaryMedias: MediasFullType =
			await Promise.all(
				uploadedMedias.filter(Boolean).map(async (m) => {
					const isVideo = m.blob?.type.startsWith("video")

					if (isVideo) {
						const videoElem = await getLoadedVideo(
							m.blob ?? new Blob(),
						)

						return {
							media_type: "video",
							resource_url: m.resource_url,
							aspect: videoElem.width / videoElem.height,
							thumb_url: null,
						}
					} else {
						const imgElem = await getLoadedImage(
							m.blob ?? new Blob(),
						)

						return {
							media_type: "image",
							resource_url: m.resource_url,
							aspect: imgElem.width / imgElem.height,
						}
					}
				}),
			)

		dispatch({
			name: "chat_add_request",
			sessionId: id,
			request: {
				name: "media_source",
				medias: temporaryMedias,
			},
			temporaryId: temporaryMessageId,
			interacted_at: dateToSortableString(new Date()),
			started: "Today",
		})

		dispatch({
			name: "chat_update_uploaded_files",
			uploadedMedias: {
				medias: uploadedMedias,
				status: "uploading",
			},
			sessionId: id,
		})

		await Promise.all(
			uploadedMedias
				.filter(Boolean)
				.map(
					async (
						uploadedMedia,
					): Promise<ChatRequestSourceMedia> => {
						if (!uploadedMedia.blob) {
							throw new Error("")
						}
						const extension =
							mimeTypeMapper[uploadedMedia.blob?.type]
						if (!extension) {
							throw new Error("file not supported")
						}

						const urls = await axios
							.post<WizardUploadResponse>(
								"/api/wizard-upload",
								{
									extension,
									session_id: id,
								},
							)
							.then((r) => r.data)
							.catch(() =>
								dispatch({
									name: "chat_update_uploaded_files",
									uploadedMedias: {
										medias: uploadedMedias,
										status: "fail",
									},
									sessionId: id,
								}),
							)

						await axios.put(
							urls?.upload ?? "",
							uploadedMedia?.blob,
						)

						const isVideo =
							uploadedMedia.blob?.type.startsWith("video")

						if (isVideo) {
							return {
								media_type: "video",
								resource_url: urls?.download ?? "",
								aspect: uploadedMedia.aspect,
								thumb_url: null,
							}
						} else {
							return {
								media_type: "image",
								resource_url: urls?.download ?? "",
								aspect: uploadedMedia.aspect,
							}
						}
					},
				),
		).then(
			async (medias) =>
				await sendMessage(
					{
						name: "media_source",
						medias,
					},
					true,
				).then(() =>
					dispatch({
						name: "chat_update_uploaded_files",
						uploadedMedias: {
							medias: uploadedMedias,
							status: "done",
						},
						sessionId: id,
					}),
				),
		)
	}

	async function populateSession(id: number) {
		await axios
			.post<ChatListResponse>("/api/wizard-list-chat", {
				session_id: id,
			})
			.then((r) => {
				const updatedMessages = r.data

				let errorType: ErrorType | undefined = undefined

				if (updatedMessages.session_status === "error") {
					errorType = "unknown"
				} else if (
					updatedMessages.session_status ===
					"retryable_error"
				) {
					errorType = "retry"
				}

				dispatch({
					id,
					name: "chat_populate_messages",
					sessionStatus: updatedMessages.session_status,
					messages: updatedMessages.messages,
					sessionName: updatedMessages.session_name,
					sessionInteractedAt:
						updatedMessages.session_interacted_at,
					errorType,
				})
			})
	}

	const profilePicture = userInfo.details.profilePic

	async function renameSession(
		id: number,
		newName: string,
	) {
		dispatch({
			name: "chat_change_session_name",
			sessionId: id,
			newName,
		})

		await axios.post("/api/wizard-rename-session", {
			session_id: id,
			name: newName,
		})
	}

	async function remakeSession(id: number) {
		const temporaryId = Math.round(
			Math.random() * -1_000_000,
		)
		dispatch({
			name: "chat_add_new_session",
			newSession: {
				id: temporaryId,
				name: "New Session",
				interacted_at: dateToSortableString(new Date()),
				started: "Today",
				messages: [],
				status: "client-loading",
				uploadedMedias: {
					medias: [],
					status: "idle",
				},
				thumbnail_url: null,
			},
		})

		await axios
			.post<StartSessionResponse>(
				"/api/wizard-remake-session",
				{
					session_id: id,
				},
			)
			.then((response) => {
				const newSession: SessionType = {
					id: response.data.id,
					name: response.data.name,
					started: getDateCategory(
						response.data.interacted_at,
					),
					status: response.data.status,
					interacted_at: response.data.interacted_at,
					messages: response.data.messages
						.map((m) => {
							return {
								...m,
								fakeId: m.id,
							}
						})
						.reverse(),
					uploadedMedias: {
						medias: [],
						status: "idle",
					},
					thumbnail_url: null,
				}

				dispatch({
					name: "chat_change_temp_session",
					newSession,
					temporaryId,
				})

				pushSessionUrl(response.data.id)
			})
			.catch((e) => {
				if (e instanceof AxiosError) {
					getErrorMessage(e, temporaryId)
				}
			})
	}

	async function deleteSession(id: number) {
		dispatch({
			name: "chat_delete_session",
			sessionId: id,
		})

		if (state.chat.selectedSession === id) {
			clearChatScreen()
		}

		await axios.post("/api/wizard-delete-session", {
			session_id: id,
		})
	}

	function enterShortCut(id: number, name: string) {
		dispatch({
			name: "chat_change_session_name",
			sessionId: id,
			newName: name,
		})

		renameSession(id, name)
	}

	const pushSessionUrl = useCallback(
		function (id: number) {
			const query = {
				...router.query,
				session_id: id,
			}

			const params = Object.entries(query)
				.map(([key, value]) => {
					if (
						key !== "initial_message" &&
						key !== "feature"
					) {
						if (id) {
							return `${encodeURIComponent(
								key,
							)}=${encodeURIComponent(value)}`
						}
					}
				})
				.join("&")

			router.push(`/chat?${params}`)
		},
		[router],
	)

	async function selectSession(id: number) {
		if (state.activePage !== "chat") {
			changeSelectedPage("chat")
		}
		const selectedSession = state.chat.content[id]
		if (!selectedSession) {
			console.error("How did you do this?")
			return
		}

		dispatch({
			name: "chat_select_session",
			sessionId: id,
		})

		const isRealId = id > 0
		if (isRealId) {
			pushSessionUrl(id)
		}

		if (selectedSession.status === "waiting") {
			sendMessage({ name: "continue" }, true, id)
		}

		if (!selectedSession?.messages) {
			await populateSession(id)
		}
	}

	async function loadMoreSessions(offset: number) {
		const result = await getChatSessions(offset)

		if (state.activePage === "videoGpt") {
			dispatch({
				name: "add_video_gpt_content",
				newContent: result.map((session) => {
					return {
						id: session.id,
						name: session.name,
						interacted_at: session.interacted_at,
						thumbnail_url: session.thumbnail_url,
						messages: session.messages,
						uploadedMedias: session.uploadedMedias,
						started: session.started,
						status: session.status,
					}
				}),
			})
		} else if (state.activePage === "chat") {
			dispatch({
				name: "chat_add_new_group",
				newSessions: result,
			})
		}

		return result.map((x) => x.id)
	}

	async function getChatSessions(offset: number) {
		const result = await axios
			.get<SessionType[]>("/api/wizard-list-sessions", {
				params: {
					offset,
					limit: SESSION_LIMIT,
				},
			})
			.then((res) => res.data)

		return result
	}

	const pendingSessions = Object.values(state.chat.content)
		.filter((session) => session?.status === "visualizing")
		.map((filteredSession) => filteredSession?.id)
		.filter(Boolean)

	async function checkSessionStatuses() {
		if (pendingSessions.length === 0) {
			return
		}

		const { statuses } = await axios
			.post<JobStatusesResponse>(
				"/api/wizard-job-statuses",
				{
					session_ids: pendingSessions,
				},
			)
			.then((r) => r.data)

		const updatedSessions = statuses
			.filter(
				(s) =>
					s.status === "completed" &&
					state.chat.content[s.session_id]?.status !==
						"deleted",
			)
			.map((s) => s.session_id)

		dispatch({
			name: "chat_update_sessions",
			updatedSessions,
		})

		if (
			state.chat.selectedSession &&
			updatedSessions.includes(state.chat.selectedSession)
		) {
			sendMessage({ name: "continue" })
		}
	}

	useQuery({
		queryKey: ["visualizing-check", pendingSessions],
		queryFn: checkSessionStatuses,
		refetchInterval: 5000,
		enabled: pendingSessions.length > 0,
	})

	const selectedSession = state.chat.selectedSession
		? state.chat.content[state.chat.selectedSession] ?? null
		: null

	const isActiveSessionGeneratingOrLoading =
		selectedSession?.status === "generating" ||
		selectedSession?.status === "loading" ||
		selectedSession?.status === "waiting" ||
		selectedSession?.status === "analyzing" ||
		lastResponse?.name === "visualizing" ||
		lastResponse?.name === "generating" ||
		lastResponse?.name === "analyzing"

	useQuery({
		queryKey: [
			"generating-check",
			isActiveSessionGeneratingOrLoading,
			state.chat.selectedSession,
		],
		queryFn: checkListChatWhenGenerating(),
		refetchInterval: 5000,
		enabled: isActiveSessionGeneratingOrLoading,
	})

	function checkListChatWhenGenerating() {
		return async function () {
			if (!state.chat.selectedSession) {
				console.error("No state")
				return
			}
			if (
				state.chat.content[state.chat.selectedSession]
					?.status !== "deleted"
			) {
				await populateSession(state.chat.selectedSession)
			}
		}
	}

	async function duplicateSession() {
		const id = state.chat.selectedSession
		if (!id) return
		const session = state.chat.content[id]
		if (!session) {
			return
		}
		let name = session.name
		if (name !== "New Session") {
			name += " (copy)"
		}
		const temporaryId = Math.round(
			Math.random() * -1_000_000,
		)
		dispatch({
			name: "chat_add_new_session",
			newSession: {
				id: temporaryId,
				name,
				interacted_at: dateToSortableString(new Date()),
				started: "Today",
				messages: [],
				status: "client-loading",
				uploadedMedias: {
					medias: [],
					status: "idle",
				},
				thumbnail_url: null,
			},
		})

		await axios
			.post<StartSessionResponse>(
				"/api/wizard-duplicate-session",
				{
					session_id: id,
				},
			)
			.then((response) => {
				const messages = response.data.messages
					.map((m) => {
						return {
							...m,
							fakeId: m.id,
						}
					})
					.reverse()

				if (messages.length !== 0) {
					dispatch({
						name: "chat_change_temp_session",
						newSession: {
							...response.data,
							messages,
							started: "Today",
							uploadedMedias: {
								medias: [],
								status: "idle",
							},
							thumbnail_url: null,
						},
						temporaryId,
					})
				}
			})
	}

	async function resetSession(next: boolean) {
		const id = state.chat.selectedSession
		if (!id) return

		await axios
			.post<StartSessionResponse>(
				"/api/wizard-reset-session",
				{
					next,
					session_id: id,
				},
			)
			.then((response) => {
				const messages = response.data.messages
					.map((m) => {
						return {
							...m,
							fakeId: m.id,
						}
					})
					.reverse()

				if (messages.length !== 0) {
					dispatch({
						name: "chat_reset_session",
						messages,
						sessionId: id,
						status: response.data.status,
					})
				}
			})
	}

	const getErrorMessage = useCallback(async function (
		e: AxiosError,
		id: number,
		temporaryMessageId?: number,
	) {
		const status = e.response?.status
		switch (status) {
			case 430:
				await populateSession(id).catch(() =>
					console.error("couldnt refresh"),
				)
				break
			case 412:
				dispatch({
					name: "chat_set_session_status",
					sessionId: id,
					sessionStatus: "retryable_error",
					errorType: "retry",
					temporaryMessageId,
				})
				break
			case 431:
				dispatch({
					name: "chat_set_session_status",
					sessionId: id,
					sessionStatus: "error",
					errorType: "upgrade",
				})
				break
			case 432:
				dispatch({
					name: "chat_set_session_status",
					sessionId: id,
					sessionStatus: "error",
					errorType: "upgrade-tokens",
				})
				break
			default:
				dispatch({
					name: "chat_set_session_status",
					sessionId: id,
					sessionStatus: "error",
					errorType: "unknown",
				})
				break
		}
	},
	[])

	const setHomeState = useCallback(async () => {
		if (state.home.content.length !== 0) {
			dispatch({
				name: "home_state",
				history: state.home.content,
				filter: state.home.filter,
			})
		} else {
			dispatch({
				name: "home_state",
				history: [],
			})

			await fetchVideos(
				[
					"deform",
					"ai_restyle",
					"ai_photo",
					"text_to_image",
					"text_to_video",
				],
				0,
			).then((r) =>
				dispatch({
					name: "home_state",
					history: r,
				}),
			)
		}
	}, [state.home.content, state.home.filter])

	const setDiscoverState = useCallback(async () => {
		const [
			availableFiltersGallery,
			availableFiltersTemplates,
		] = await Promise.all([
			axios
				.get<GalleryFiltersResponse>("/api/gallery-filters")
				.then((r) => r.data),
			axios
				.get<TutorialFiltersResponse>(
					"/api/tutorial-filters",
				)
				.then((r) => r.data),
		])

		dispatch({
			name: "discover_state",
			availableFiltersGallery,
			availableFiltersTemplates,
		})
	}, [])

	const setChatState = useCallback(async () => {
		if (state.chat.sessionsGroup.length !== 0) {
			dispatch({
				name: "chat_state",
				content: state.chat.content,
			})
		} else if (state.videoGpt.content.length !== 0) {
			dispatch({
				name: "chat_state",
				content: createSessionsRecord(
					state.videoGpt.content,
				),
			})
		} else {
			const sessions = await getChatSessions(0)

			dispatch({
				name: "chat_state",
				content: sessions,
			})
		}
	}, [
		state.chat.content,
		state.chat.sessionsGroup.length,
		state.videoGpt.content,
	])

	async function getPopupsData() {
		return await axios
			.get<WizardGetInputsResponse>(
				"/api/wizard-get-inputs",
			)
			.then((r) => r.data)
	}

	async function getCaptionsData() {
		return await axios
			.post<CaptionsResponse>("/api/caption-templates", {
				offset: 0,
				limit: 40,
			})
			.then((r) => r.data)
	}

	const setVideoGptState = useCallback(async () => {
		if (state.videoGpt.content.length !== 0) {
			dispatch({
				name: "video_gpt_state",
				projectList: state.videoGpt.content,
			})
		} else {
			const projectList = await getChatSessions(0)
			dispatch({
				name: "video_gpt_state",
				projectList: projectList.map((session) => {
					return {
						id: session.id,
						name: session.name,
						messages: null,
						status: session.status,
						started: session.started,
						interacted_at: session.interacted_at,
						uploadedMedias: session.uploadedMedias,
						thumbnail_url: session.thumbnail_url,
					}
				}),
			})
		}

		if (state.videoGpt.popupsData === null) {
			const [popupsData, captions] = await Promise.all([
				getPopupsData(),
				getCaptionsData(),
			])
			dispatch({
				name: "load_popups_data",
				data: popupsData,
				captions,
			})
		}
	}, [state.videoGpt.content, state.videoGpt.popupsData])

	const changeSelectedPage = useCallback(
		async (pageName: PageName) => {
			switch (pageName) {
				case "home":
					pushUrl("home")
					setHomeState()
					break

				case "videoGpt":
					pushUrl("video-gpt")
					setVideoGptState()
					break

				case "chat":
					pushUrl("chat")
					setChatState()
					break

				case "discover":
					pushUrl("discover")
					setDiscoverState()
					break
			}
		},
		[
			setVideoGptState,
			setChatState,
			setHomeState,
			setDiscoverState,
		],
	)

	const startNewSession = useCallback(
		async function (
			options: StartSessionRequest["context"],
			inputs: StartSessionWithInputsRequest | null,
		) {
			if (state.activePage !== "chat") {
				changeSelectedPage("chat")
			}
			const isDebug =
				process.env.NEXT_PUBLIC_DEBUG === "true"

			const temporaryId = Math.round(
				Math.random() * -1_000_000,
			)
			const temporaryMessageId = Math.round(
				Math.random() * -1_000_000,
			)
			const messages: ChatMessage[] = []
			if (options.initial_message) {
				messages.push({
					id: temporaryMessageId,
					sent_at: dateToSortableString(new Date()),
					type: "request",
					name: "text",
					message: options.initial_message,
				})
			} else if (inputs) {
				if (inputs.message.type === "medias") {
					messages.push({
						id: temporaryMessageId,
						sent_at: dateToSortableString(new Date()),
						type: "request",
						name: "media_source",
						medias: inputs.message.resources,
					})
				} else {
					messages.push({
						id: temporaryMessageId,
						sent_at: dateToSortableString(new Date()),
						type: "request",
						name: "text",
						message: inputs.message.content,
					})
				}
			}

			dispatch({
				name: "chat_add_new_session",
				newSession: {
					id: temporaryId,
					name: "New Session",
					interacted_at: dateToSortableString(new Date()),
					started: "Today",
					messages,
					status: "client-loading",
					uploadedMedias: {
						medias: [],
						status: "idle",
					},
					thumbnail_url: null,
				},
			})

			if (inputs) {
				await axios
					.post<StartSessionResponse>(
						"/api/wizard-start-session-with-inputs",
						inputs,
					)
					.then((response) => {
						const newSession: SessionType = {
							id: response.data.id,
							name: response.data.name,
							started: getDateCategory(
								response.data.interacted_at,
							),
							status: response.data.status,
							interacted_at: response.data.interacted_at,
							messages: response.data.messages
								.map((m) => {
									return {
										...m,
										fakeId: m.id,
									}
								})
								.reverse(),
							uploadedMedias: {
								medias: [],
								status: "idle",
							},
							thumbnail_url: null,
						}

						dispatch({
							name: "chat_change_temp_session",
							newSession,
							temporaryId,
						})

						if (
							state.chat.selectedSession === null &&
							state.activePage === "chat"
						) {
							pushSessionUrl(response.data.id)
						}
					})
					.catch((e) => {
						if (e instanceof AxiosError) {
							getErrorMessage(e, temporaryId)
						}
					})
			} else {
				await axios
					.post<StartSessionResponse>(
						"/api/wizard-start-session",
						{
							context: {
								website_url: options.website_url,
								initial_message: options.initial_message,
							},
							group: state.chat.group?.id ?? null,
							tag:
								isDebug || state.chat.group ? "test" : null,
						},
					)
					.then((response) => {
						const newSession: SessionType = {
							id: response.data.id,
							name: response.data.name,
							started: getDateCategory(
								response.data.interacted_at,
							),
							status: response.data.status,
							interacted_at: response.data.interacted_at,
							messages: response.data.messages
								.map((m) => {
									return {
										...m,
										fakeId: m.id,
									}
								})
								.reverse(),
							uploadedMedias: {
								medias: [],
								status: "idle",
							},
							thumbnail_url: null,
						}

						dispatch({
							name: "chat_change_temp_session",
							newSession,
							temporaryId,
						})

						if (state.chat.selectedSession === null) {
							pushSessionUrl(response.data.id)
						}
					})
					.catch((e) => {
						if (e instanceof AxiosError) {
							getErrorMessage(e, temporaryId)
						}
					})
			}
		},
		[
			state.activePage,
			state.chat.selectedSession,
			state.chat.group,
			changeSelectedPage,
			pushSessionUrl,
			getErrorMessage,
		],
	)

	async function retrySession() {
		if (!state.chat.selectedSession) return

		const lastMessage =
			state.chat.content[state.chat.selectedSession ?? -1]
				?.messages

		if (!lastMessage) return

		const lastMessageId = lastMessage.at(0)?.id

		if (!lastMessageId) return

		const changedStatus = await axios
			.post<{ status: SessionStatus }>(
				"/api/wizard-retry-session",
				{
					session_id: state.chat.selectedSession,
				},
			)
			.then((r) => r.data.status)

		dispatch({
			name: "chat_retry_session",
			sessionId: state.chat.selectedSession,
			temporaryMessageId: lastMessageId,
			status: changedStatus,
		})
	}

	async function sendMessage(
		request: ChatRequest,
		dontAddRequest?: boolean,
		sessionId?: number,
	) {
		if (!state.chat.selectedSession) {
			console.error(
				"State is null and you are trying to send message",
			)
			return
		}
		const id = sessionId ?? state.chat.selectedSession

		const sessionStatus = state.chat.content[id]?.status

		if (sessionStatus === "deleted") return

		const lastMessage =
			state.chat.content[id]?.messages?.at(0)
		const lastMessageId = lastMessage?.id
		const lastMessageType = lastMessage?.type

		if (
			lastMessageId === undefined ||
			lastMessageType === "request"
		) {
			return
		}
		const isDebug = process.env.NEXT_PUBLIC_DEBUG === "true"

		const payload: SendMessageRequest = {
			session_id: id,
			request,
			last_response_id: lastMessageId,
			tag: isDebug || state.chat.group ? "test" : null,
		}

		const temporaryId = Math.random() * -1_000_000
		if (!dontAddRequest) {
			dispatch({
				name: "chat_add_request",
				request,
				temporaryId,
				sessionId: id,
				interacted_at: dateToSortableString(new Date()),
				started: "Today",
			})
		}

		await axios
			.post<SendMessageResponse>(
				"/api/wizard-send-message",
				payload,
				{ timeout: 120000 },
			)
			.then((r) => {
				dispatch({
					name: "chat_add_response",
					sessionId: id,
					status: r.data.session_status,
					response: r.data.response,
					responseId: r.data.response.id,
					sentAt: r.data.response.sent_at,
					requestUpdate: {
						id: temporaryId,
						realId: r.data.request.id,
						sentAt: r.data.request.sent_at,
					},
					sessionNameChanged: r.data.session_name_changed,
					newSessionName: r.data.new_session_name,
				})

				dispatch({
					name: "chat_set_session_status",
					sessionId: id,
					sessionStatus: r.data.session_status,
				})
			})
			.catch(async (e) => {
				if (e instanceof AxiosError) {
					getErrorMessage(e, id, temporaryId)
				}
			})
	}

	useEffect(() => {
		if (
			initialData.name === "chat" &&
			initialData.initialPage.initialMessage &&
			isFirstTime.current
		) {
			isFirstTime.current = false
			const message = initialData.initialPage.initialMessage
			const context = isStringUrl(message)

			startNewSession(context, null)
		}
	}, [
		initialData.initialPage,
		initialData.name,
		startNewSession,
	])

	function clearChatScreen() {
		dispatch({ name: "chat_empty_state" })
		const query = {
			...router.query,
		}

		const params = Object.entries(query)
			.map(([key, value]) => {
				if (
					key !== "initial_message" &&
					key !== "session_id"
				) {
					return `${key}=${value}`
				}
			})
			.join("&")

		router.push(`/chat?${params}`)
	}

	function changeShowRatingValue() {
		if (state.chat.selectedSession === null) {
			return
		}

		dispatch({
			name: "chat_change_rating_value",
			id: state.chat.selectedSession,
		})
	}

	function changeActiveChild(name: string | null) {
		dispatch({ name: "change_child_name", childName: name })
	}

	async function refetchNewJobs(limit: number) {
		let filter: AITool[] = [
			"deform",
			"ai_restyle",
			"ai_photo",
			"text_to_image",
			"text_to_video",
		]
		if (state.activePage === "home") {
			filter = state.home.filter
		}

		const newContent = await fetchVideos(filter, 0, limit)
		dispatch({
			name: "add_ai_studio_content_from_start",
			newContent,
		})
	}

	const inProgressJobIds = useRef<number[]>([])
	const lastQuery = useQuery({
		queryFn: fetchLast,
		queryKey: ["deforum-last", "user", "pro"],
		refetchInterval: STATUS_UPDATE_MILLIS,
		onSuccess(data) {
			if (!data) {
				return
			}

			const newIds = new Set(data.map(({ id }) => id))
			const jobNotInProgress =
				inProgressJobIds.current.filter(
					(inProgressJobId) => !newIds.has(inProgressJobId),
				).length
			if (jobNotInProgress !== 0) {
				refetchNewJobs(jobNotInProgress)
			}
			inProgressJobIds.current = [...newIds.values()]
		},
	})

	const currentRunningJobs =
		lastQuery.data?.filter((job) => {
			return (
				job.status === "in-queue" ||
				job.status === "in-progress"
			)
		}).length ?? -1

	const { hasConcurrentJobs } = getUserEntitlements(
		userInfo.entitlements,
	)

	const canTryAgain =
		currentRunningJobs <
		getConcurrentJobCount(hasConcurrentJobs)

	async function deleteHistoryItem(id: number) {
		dispatch({
			name: "delete_history_item",
			id,
		})

		await axios
			.post("/api/deforum-delete", { id })
			.catch(() => console.error("cant delete"))
	}

	function selectAiStudioTool(tool: AITool | null) {
		if (tool === null) {
			window.history.pushState(
				{ path: `/home` },
				"",
				`/home`,
			)
		} else {
			window.history.pushState(
				{ path: `/home?tool=${tool}` },
				"",
				`/home?tool=${tool}`,
			)
		}

		dispatch({
			name: "select_ai_studio_tool",
			selectedTool: tool,
		})
	}

	async function loadMoreHistoryContent(offset: number) {
		let filter: AITool[] = [
			"deform",
			"ai_restyle",
			"ai_photo",
			"text_to_image",
			"text_to_video",
		]
		if (state.activePage === "home") {
			filter = state.home.filter
		}

		const result = await fetchVideos(filter, offset)

		dispatch({
			name: "add_ai_studio_content",
			newContent: result,
		})

		return result.map((x) => x.id)
	}

	function pushUrl(page: PagesUrlNames) {
		if (typeof window !== undefined) {
			window.history.pushState(`/${page}`, "", `/${page}`)
		}
	}

	function changeProjectsDisplay(
		display: "column" | "grid",
	) {
		dispatch({
			name: "change_projects_display",
			display,
		})
	}

	function changeDiscoverFilter(
		filter: Pages,
		q?: string,
		categoryId?: string,
	) {
		let query: {
			filter: Pages
			q?: string
			categoryId?: string
		} = {
			filter,
		}
		if (q) {
			query = {
				...query,
				q,
			}
		}
		if (categoryId) {
			query = {
				...query,
				categoryId,
			}
		}
		if (typeof window !== undefined) {
			const params = queryfy(query)

			const newUrl = `/discover${params}`
			window.history.pushState({ path: newUrl }, "", newUrl)
		}

		dispatch({
			name: "change_discover_filter",
			filter,
			q,
			categoryId,
		})
	}

	const chatController: ChatController = {
		clearChatScreen,
		changeShowRatingValue,
		lastMessageId,
		selectMediaCreator,
		retrySession,
		remakeSession,
		resetSession,
		duplicateSession,
		initializaeUploadedMedias,
		appendFileCreator,
		removeUploadedFile,
		uploadAndSendMedias,
		enterShortCut,
		profilePicture,
		lastResponse,
		sendMessage,
		deleteSession,
		renameSession,
		loadMoreSessions,
		startNewSession,
		selectSession,
	}

	return {
		changeSelectedPage,
		deleteHistoryItem,
		changeProjectsDisplay,
		selectAiStudioTool,
		changeDiscoverFilter,
		home: state.home,
		videoGpt: state.videoGpt,
		chat: state.chat,
		discover: state.discover,
		activePage: state.activePage,
		loadMoreHistoryContent,
		activeChild: state.activeChild,
		changeActiveChild,
		lastQuery,
		canTryAgain,
		chatController,
	}
}

function filterAndConcatHistory(
	oldContent: HistoryEntity[],
	newContent: HistoryEntity[],
	newContentFromBeginning: boolean,
): HistoryEntity[] {
	const newContentIds = new Set(
		newContent.map(({ id }) => id),
	)
	const oldContentFiltered = oldContent.filter(
		(old) => !newContentIds.has(old.id),
	)

	if (newContentFromBeginning) {
		return newContent
			.concat(oldContentFiltered)
			.sort(
				(a, b) =>
					new Date(
						new Date(b.endTime ?? new Date()),
					).getTime() -
					new Date(
						new Date(a.endTime ?? new Date()),
					).getTime(),
			)
	} else {
		return oldContentFiltered
			.concat(newContent)
			.sort(
				(a, b) =>
					new Date(
						new Date(b.endTime ?? new Date()),
					).getTime() -
					new Date(
						new Date(a.endTime ?? new Date()),
					).getTime(),
			)
	}
}

function removeIfExist(
	oldContent: SessionType[],
	newContent: SessionType[],
): SessionType[] {
	const sessionMap = new Map<number, SessionType>()

	oldContent.forEach((session) => {
		sessionMap.set(session.id, session)
	})

	newContent.forEach((session) => {
		sessionMap.set(session.id, session)
	})

	const result = Array.from(sessionMap.values()).sort(
		(a, b) =>
			new Date(new Date(b.interacted_at)).getTime() -
			new Date(new Date(a.interacted_at)).getTime(),
	)

	return result
}

function createPagesData(
	initialData: DashboardControllerInitialData,
): DashboardReducerState {
	const { initialPage, name } = initialData
	const emptyChat: ChatPageType = {
		content: [],
		sessionsGroup: [],
		selectedSession: null,
		initialInputs: null,
		initialMessage: null,
		group: null,
	}
	const emptyHome: HomePageType = {
		content: [],
		projectsDisplay: "column",
		text: "",
		popupsData: null,
	}
	const emptyDiscover: DiscoverPageType = {
		content: {
			initialGallery: null,
			initialFiltersGallery: null,
			initialTemplates: null,
			initialFiltersTemplates: null,
			initialAdExamples: null,
			initialFiltersAdExamples: null,
			availableFiltersGallery: null,
			availableFiltersTemplates: null,
		},
		filter: "ad-examples",
		q: null,
		categoryId: null,
	}
	const emptyAiStudio: AiStudioPageType = {
		content: [],
		filter: [
			"deform",
			"ai_restyle",
			"ai_photo",
			"text_to_image",
			"text_to_video",
		],
		selectedTool: null,
	}
	switch (name) {
		case "home":
			return {
				activePage: "home",
				activeChild: null,
				home: initialPage,
				videoGpt: emptyHome,
				discover: emptyDiscover,
				chat: emptyChat,
			}

		case "videoGpt":
			return {
				activePage: "videoGpt",
				activeChild: null,
				home: emptyAiStudio,
				videoGpt: initialPage,

				chat: {
					content: createSessionsRecord(
						initialPage.content,
					),
					sessionsGroup: [],
					selectedSession: null,
					initialInputs: null,
					initialMessage: null,
					group: null,
				},
				discover: emptyDiscover,
			}
		case "chat":
			return {
				activePage: "chat",
				activeChild: null,
				chat: {
					content: initialPage.content,
					sessionsGroup: initialPage.sessionsGroup,
					selectedSession: initialPage.selectedSession,
					initialInputs: initialPage.initialInputs,
					initialMessage: initialPage.initialMessage,
					group: initialPage.group,
				},
				discover: emptyDiscover,
				home: emptyAiStudio,
				videoGpt: emptyHome,
			}
		case "discover":
			return {
				activePage: "discover",
				activeChild: null,
				discover: initialPage,
				chat: emptyChat,
				home: emptyAiStudio,
				videoGpt: emptyHome,
			}
	}
}

export function extractSessionsToGroups(
	newSessions: SessionType[],
	existingGroups: SessionsGroupType[],
): SessionsGroupType[] {
	const sessionSet = new Set<number>()
	const existingTodayGroup = existingGroups.find(
		(g) => g.started === "Today",
	)?.sessions
	const todayGroup = createGroup(
		"Today",
		newSessions,
		existingTodayGroup ?? [],
		sessionSet,
	)
	const existingThisWeekGroup = existingGroups.find(
		(g) => g.started === "This week",
	)?.sessions
	const thisWeekGroup = createGroup(
		"This week",
		newSessions,
		existingThisWeekGroup ?? [],
		sessionSet,
	)
	const existingThisMonthGroup = existingGroups.find(
		(g) => g.started === "This month",
	)?.sessions
	const thisMonthGroup = createGroup(
		"This month",
		newSessions,
		existingThisMonthGroup ?? [],
		sessionSet,
	)
	const existingThisYearGroup = existingGroups.find(
		(g) => g.started === "This year",
	)?.sessions
	const thisYearGroup = createGroup(
		"This year",
		newSessions,
		existingThisYearGroup ?? [],
		sessionSet,
	)

	const resultSesionsGroups: SessionsGroupType[] = [
		todayGroup,
		thisWeekGroup,
		thisMonthGroup,
		thisYearGroup,
	]

	return resultSesionsGroups
}

const filterArray = <K, T extends { id: K }>(
	pages: T[],
	dataSet = new Set<K>(),
): T[] => {
	return pages.filter((page) => {
		if (dataSet.has(page.id)) {
			return false
		}

		dataSet.add(page.id)
		return true
	})
}

function createGroup(
	groupName: DateCategories,
	sessions: SessionType[],
	existingGroup: {
		id: number
		name: string
		interacted_at: string
	}[],
	sessionSet: Set<number>,
): SessionsGroupType {
	const resultsesions: {
		id: number
		name: string
		interacted_at: string
	}[] = filterArray(
		sessions
			.filter((s) => s.started === groupName)
			.map((g) => {
				return {
					id: g.id,
					name: g.name,
					interacted_at: g.interacted_at,
				}
			})
			.concat(existingGroup)
			.sort(
				(a, b) =>
					new Date(b.interacted_at).getTime() -
					new Date(a.interacted_at).getTime(),
			),
		sessionSet,
	)

	const resultGroup: SessionsGroupType = {
		started: groupName,
		sessions: resultsesions,
	}

	return resultGroup
}

type DateCategories =
	| "Today"
	| "This week"
	| "This month"
	| "This year"

export function getDateCategory(
	dateString: string,
): DateCategories {
	const currentDate = new Date()
	const inputDate = new Date(dateString)

	const currentYear = currentDate.getFullYear()
	const currentMonth = currentDate.getMonth()
	const currentWeek = getWeekNumber(currentDate)
	const currentDay = currentDate.getDate()

	const inputYear = inputDate.getFullYear()
	const inputMonth = inputDate.getMonth()
	const inputWeek = getWeekNumber(inputDate)
	const inputDay = inputDate.getDate()

	if (currentYear === inputYear) {
		if (currentMonth === inputMonth) {
			if (currentDay === inputDay) {
				return "Today"
			} else if (currentWeek === inputWeek) {
				return "This week"
			} else {
				return "This month"
			}
		} else {
			return "This year"
		}
	} else {
		return "This year"
	}
}

function getWeekNumber(date: Date): number {
	const d = new Date(
		Date.UTC(
			date.getFullYear(),
			date.getMonth(),
			date.getDate(),
		),
	)
	d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7))
	const yearStart = new Date(
		Date.UTC(d.getUTCFullYear(), 0, 1),
	)
	const weekNo = Math.ceil(
		((d.valueOf() - yearStart.valueOf()) / 86400000 + 1) /
			7,
	)
	return weekNo
}
